// src/NotFound.js
import React from 'react';
import { Link, Container, Box, Typography, Button, Paper } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import CoffeeIcon from '@mui/icons-material/Coffee';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import keywordsData from '../lang/keywords.json';

const NotFound = ({ keywordsData, language }) => {
  const isArabic = language === 'ar';

  return (
    <Container 
      maxWidth="md" 
      sx={{ 
        py: 8,
        px: 2,
        mb:10,
        backgroundColor: '#000000',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#FFFFFF'
      }}
    >
      <Paper 
        elevation={0}
        sx={{ 
          p: { xs: 4, md: 6 },
          backgroundColor: '#0D0D0D',
          border: '1px solid #2D2D2D',
          borderRadius: '32px',
          boxShadow: '0 12px 40px rgba(255, 215, 0, 0.15)',
          transition: 'all 0.3s ease',
          position: 'relative',
          overflow: 'hidden',
          '&:hover': {
            border: '1px solid #FFD700',
            boxShadow: '0 16px 48px rgba(255, 215, 0, 0.25)',
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: 'linear-gradient(90deg, #FFD700, #FFE55C, #FFD700)',
          }
        }}
      >
        {/* Decorative Coffee Beans */}
        <Box sx={{
          position: 'absolute',
          top: 20,
          right: 20,
          opacity: 0.1,
          transform: 'rotate(15deg)',
          display: { xs: 'none', sm: 'block' }
        }}>
          <CoffeeIcon sx={{ fontSize: 80, color: '#FFD700' }} />
        </Box>
        <Box sx={{
          position: 'absolute',
          bottom: 20,
          left: 20,
          opacity: 0.1,
          transform: 'rotate(-15deg)',
          display: { xs: 'none', sm: 'block' }
        }}>
          <CoffeeIcon sx={{ fontSize: 60, color: '#FFD700' }} />
        </Box>

        {/* Home Link */}
        <Box sx={{ mb: 4 }}>
          <Button
            component={Link}
            href="../../"
            sx={{
              color: '#B3B3B3',
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 600,
              '&:hover': {
                color: '#FFD700',
                backgroundColor: 'rgba(255, 215, 0, 0.08)',
                '& .MuiSvgIcon-root': {
                  color: '#FFD700',
                }
              },
              transition: 'all 0.2s ease'
            }}
            startIcon={<HomeIcon sx={{ color: '#B3B3B3' }} />}
          >
            {isArabic ? keywordsData.home_txt_ar : keywordsData.home_txt_en}
          </Button>
        </Box>

        {/* Main Content */}
        <Box 
          sx={{ 
            textAlign: 'center',
            position: 'relative',
            zIndex: 1
          }}
        >
          {/* Error Icon */}
          <Box sx={{
            width: 120,
            height: 120,
            borderRadius: '50%',
            backgroundColor: 'rgba(255, 215, 0, 0.08)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px',
            border: '2px dashed #FFD700',
            animation: 'pulse 2s infinite',
            '@keyframes pulse': {
              '0%': {
                transform: 'scale(1)',
                opacity: 1,
              },
              '50%': {
                transform: 'scale(1.05)',
                opacity: 0.8,
              },
              '100%': {
                transform: 'scale(1)',
                opacity: 1,
              },
            }
          }}>
            <ErrorOutlineIcon sx={{ fontSize: 64, color: '#FFD700' }} />
          </Box>

          {/* 404 Number */}
          <Typography 
            variant="h1" 
            sx={{ 
              fontSize: { xs: '6rem', md: '8rem' },
              fontWeight: 900,
              color: '#FFD700',
              lineHeight: 1,
              mb: 2,
              textShadow: '0 0 20px rgba(255, 215, 0, 0.3)',
              letterSpacing: '8px'
            }}
          >
            404
          </Typography>

          {/* Title */}
          <Typography 
            variant="h3" 
            sx={{ 
              color: '#FFFFFF',
              fontWeight: 800,
              mb: 2,
              fontSize: { xs: '1.8rem', md: '2.2rem' }
            }}
          >
            {isArabic ? keywordsData.page_not_found_title_ar : keywordsData.page_not_found_title_en}
          </Typography>

          {/* Description */}
          <Typography 
            variant="h6" 
            sx={{ 
              color: '#B3B3B3',
              fontWeight: 500,
              mb: 4,
              maxWidth: '500px',
              mx: 'auto',
              lineHeight: 1.8,
              fontSize: { xs: '1rem', md: '1.1rem' }
            }}
          >
            {isArabic ? keywordsData.page_not_found_desc_ar : keywordsData.page_not_found_desc_en}
          </Typography>

          {/* Cafe Message */}
          <Box sx={{ 
            mb: 4,
            p: 2,
            backgroundColor: 'rgba(255, 215, 0, 0.05)',
            borderRadius: '16px',
            border: '1px solid #2D2D2D',
            maxWidth: '400px',
            mx: 'auto'
          }}>
            <Typography variant="body1" sx={{ color: '#FFD700', fontWeight: 600, mb: 0.5 }}>
              ☕ {isArabic ? 'فنجان قهوة ضائع؟' : 'A lost coffee cup?'}
            </Typography>
            <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
              {isArabic 
                ? 'لا تقلق، سنساعدك في إيجاد طريقك إلى أفضل قهوة!' 
                : 'Don\'t worry, we\'ll help you find your way to the best coffee!'}
            </Typography>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            justifyContent: 'center',
            flexDirection: { xs: 'column', sm: 'row' }
          }}>
            <Button
              component={Link}
              href="../../"
              variant="contained"
              size="large"
              startIcon={<HomeIcon />}
              sx={{
                backgroundColor: '#FFD700',
                color: '#000000',
                fontWeight: 800,
                py: 1.8,
                px: 4,
                borderRadius: '50px',
                fontSize: '1.1rem',
                border: '1px solid #FFD700',
                '&:hover': {
                  backgroundColor: '#FFE55C',
                  boxShadow: '0 6px 20px rgba(255, 215, 0, 0.4)',
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.2s ease'
              }}
            >
              {isArabic ? 'العودة إلى الرئيسية' : 'Back to Home'}
            </Button>
            
            <Button
              component={Link}
              href="../../"
              variant="outlined"
              size="large"
              startIcon={<CoffeeIcon />}
              sx={{
                color: '#FFD700',
                borderColor: '#FFD700',
                fontWeight: 700,
                py: 1.8,
                px: 4,
                borderRadius: '50px',
                fontSize: '1.1rem',
                '&:hover': {
                  borderColor: '#FFE55C',
                  backgroundColor: 'rgba(255, 215, 0, 0.08)',
                  color: '#FFE55C',
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.2s ease'
              }}
            >
              {isArabic ? 'تصفح القائمة' : 'Browse Menu'}
            </Button>
          </Box>

          {/* Help Text */}
          <Typography 
            variant="body2" 
            sx={{ 
              color: '#6B6B6B',
              mt: 4,
              fontStyle: 'italic'
            }}
          >
            {isArabic 
              ? 'إذا كنت تعتقد أن هذا خطأ، يرجى التواصل مع فريق الدعم' 
              : 'If you think this is an error, please contact support'}
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default NotFound;